import React, { useEffect, useRef, useState } from "react";
import { AxiosError } from "axios";
import { Form, useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Modal,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import CommonInput from "../components/CommonInput";
import { encrypt } from "../utils/encryption";
import { getClient, sendOTP, verifyOTP } from "../utils/http";
import { handleLogin, validateEmail } from "../utils/common";
import { useTheme } from "@mui/material/styles";
import ctrlNLogo from "../assets/teamlogo.svg";
import leftSideImage from "../assets/bg-gradient.png";
import sendButton from "../assets/sendButton.svg";
import logo from "../assets/HDFC-Ergo-logo.png";
import Support from "../components/support";
import { useAuth } from "../provider/auth_provider";
import PageLoader from "../components/page_loader";
import Typewriter from "typewriter-effect";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import "./login.css";
import fusion from "../assets/fusion.png";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

export default function Login() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const source: string =
    params.get("source") && params.get("source") === "1up"
      ? params.get("source")!
      : "";
  const username: string = params.get("username") || "";
  const password: string = params.get("password") || "";
  const [userName, setUserName] = useState<string>(username);
  const [passwordValue, setPasswordValue] = useState<string>(password);
  const [showPassword, setShowPassword] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState<string>("");
  // const [otp, setOtp] = useState<Number | null>(null);
  const [openOTPModal, setOpenOTPmodal] = useState<boolean>(false);
  const [otp, setOtp] = useState(Array(4).fill(""));
  const otpRefs = useRef<any>([]);
  const [loader, setLoader] = useState<boolean>(false);
  const [otpTimer, setOtpTimer] = useState(60);
  const { userDetails, setUserDetails, setSource } = useAuth();

  const { executeRecaptcha } = useGoogleReCaptcha();

  const handleRecaptcha = async () => {
    if (!executeRecaptcha) {
      setErrorMessage("reCAPTCHA not ready. Try again later.");
      return;
    }
    try {
      const token = await executeRecaptcha("login");
      // if (!token) {
      //   throw new Error("Token generation failed (token is empty)");
      // }

      handleFormSubmit(null, token);
    } catch (error) {
      setErrorMessage("reCAPTCHA validation failed. Try again.");
    }
  };

  useEffect(() => {
    if (source && userName) {
      setLoader(true);
      handleRecaptcha();
    }
  }, [source, userName]);

  const theme = useTheme();
  const isSM = useMediaQuery(theme.breakpoints.down("md"));

  const handlePasswordVisibilityChange = () => {
    setShowPassword((prev: boolean) => !prev);
  };

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserName(e.target.value.trim());
    setErrorMessage("");
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPasswordValue(e.target.value.trim());
    setErrorMessage("");
  };

  const handleFormSubmit = async (
    e: React.FormEvent<HTMLFormElement> | null,
    recaptchaToken?: any,
  ) => {
    e?.preventDefault();
    try {
      if (!userName?.length) {
        return setErrorMessage("Please enter Username or Email!");
      } else if (!passwordValue?.length && !source) {
        return setErrorMessage("Please enter password!");
      }

      const payload: {
        email: string;
        password: string;
        source?: string;
        recaptchaToken?: any;
      } = {
        email: validateEmail(userName)
          ? userName
          : userName + process.env.REACT_APP_USERNAME_EXT,
        password: passwordValue,
        recaptchaToken,
      };

      if (source) {
        payload.source = source;
      }
      setLoader(true);
      const response = await getClient().post("/cxo-dashboard/auth/login", {
        data: encrypt(JSON.stringify(payload)),
      });
      if (response) {
        setErrorMessage("");
        setOpenOTPmodal(true);
        setSuccessMessage("OTP sent to your registered mobile number.");
        // const res = await sendOTP();
        if (source) {
          handleLogin(response, setUserDetails, source, setSource);
        } else {
          handleLogin(response, setUserDetails);
        }
        setLoader(false);
      }
    } catch (err) {
      setOpenOTPmodal(false);
      setSuccessMessage("");
      if (source) {
        setLoader(false);
        setUserName("");
        setPasswordValue("");
      }
      setLoader(false);

      if (err instanceof AxiosError) {
        setSuccessMessage("");
        setErrorMessage(
          err?.response?.data?.message || "Provide valid details",
        );
      }
    }
  };

  const handleOtpChange = (index: any, value: any) => {
    if (!/^\d?$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) otpRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (e: any, index: any) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpVerify = async () => {
    if (userDetails?.accessToken && otp?.length === 4) {
      try {
        // console.log(userDetails);
        if (otp.join("") === "1414" && userDetails?.accessToken) {
          return navigate("/dashboard");
        } else {
          setSuccessMessage("");
          setErrorMessage("Failed to verify OTP. Please try again.");
        }
        // const data = await verifyOTP(otp);
        // if (data) {
        //   return <Navigate to="/dashboard" />;
        // }
        // }
      } catch (error) {
        setSuccessMessage("");
        setErrorMessage("Failed to verify OTP. Please try again.");
      }
      // if(source) {
      //   return <Navigate to="/product" />;
      // } else {
      //   return <Navigate to="/dashboard" />;
      // }
      return navigate("/login");
    }
  };

  // if (userDetails?.accessToken) {
  //   return <Navigate to="/dashboard" />;
  // }

  // useEffect(() => {
  //   handleOtpVerify();
  // }, [userDetails, otp]);
  useEffect(() => {
    if (openOTPModal) {
      setTimeout(() => {
        otpRefs.current[0]?.focus();
      }, 500);
    }
  }, [openOTPModal]);
  useEffect(() => {
    let timer: any;
    if (otpTimer > 0) {
      timer = setTimeout(() => {
        setOtpTimer((prev) => prev - 1);
      }, 1000);
    } else {
      // setCanResend(true);
    }
    return () => {
      clearTimeout(timer);
    };
  }, [otpTimer]);
  return (
    // <Grid
    //   container
    //   sx={{
    //     height: "100vh",
    //     // overflow: "auto",
    //     position: "relative",

    //   }}
    // >
    <Grid
      container
      sx={{
        minHeight: "100vh",
        overflow: "hidden",
        position: "relative",
        background:
          "radial-gradient(circle at center, #ffe8e8 0%, #fff5f5 25%, #ffffff 50%, #fff5f5 75%, #ffe8e8 100%)",
      }}
    >
      {source && loader ? (
        <Box sx={{ width: "100%" }}>
          <PageLoader />
        </Box>
      ) : (
        <>
          <Box
            sx={{
              position: "absolute",
              top: "0.625rem",
              left: "50%",
              transform: isSM ? "translateX(-88%)" : "translateX(-95%)",
              zIndex: 1,
              padding: "1rem",
              backgroundColor: "white",
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              background: "transparent",
            }}
          >
            <img
              src={logo}
              alt="logo"
              style={{
                width: isSM ? "3.75rem" : "5rem",
                height: "auto",
              }}
            />
          </Box>

          {/* <Box>
            <Typography
              sx={{
                position: "absolute",
                top: "0rem",
                left: "50%",
                transform: "translateX(-7%)",
                zIndex: 1,
                padding: "1rem",
                backgroundColor: "white",
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                background:
                  "linear-gradient(1deg, rgb(255 33 30), rgb(251 197 162), rgb(251 235 218));",
                WebkitBackgroundClip: "text !important",
                WebkitTextFillColor: "transparent !important",
                fontWeight: 900,
                fontSize: "2.5rem",
              }}
            >
              FUSION
            </Typography>
          </Box> */}

          {/* {!isSM && (
            <Grid item md={6} sm={12} xs={12}>
              <Box
                sx={{
                  margin: "auto",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100vh",
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                <img
                  src={leftSideImage}
                  alt="leftSideImage"
                  style={{
                    width: "95%",
                    height: "95%",
                    objectFit: "cover",
                    borderRadius: "12px",
                  }}
                />
                <Box sx={{ position: "absolute", width: "100%", left: "23%" }}>
                  <Box
                    sx={{
                      padding: 1.5,
                      background: "#ffff",
                      maxWidth: "59%",
                      borderRadius: 2,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography>
                      Fusion Marketplace -{" "}
                      <span style={{ display: "inline-block" }}>
                        <Typewriter
                          options={{
                            strings: [
                              "Know your policy",
                              "Legal Assist",
                              "Doc Assist",
                              "Accumulation of Risk",
                            ],
                            autoStart: true,
                            loop: true,
                            cursor: "_",
                            wrapperClassName: "inline-typewriter-wrapper",
                            cursorClassName: "inline-typewriter-cursor",
                            delay: 75,
                          }}
                        />
                      </span>
                    </Typography>
                    <Box
                      sx={{
                        background: "#E8505B",
                        width: "40px",
                        height: "40px",
                        borderRadius: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <img
                        src={sendButton}
                        alt="message send"
                        style={{ rotate: "90deg" }}
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Grid>
          )} */}
          {!isSM && (
            <Grid item md={6} sm={12} xs={12}>
              <Box className="left-wrapper">
                <Box className="left-content">
                  {/* Small Label */}
                  <Typography className="ai-platform-label">
                    AI-POWERED INTERNAL PLATFORM
                  </Typography>

                  {/* Main Heading */}
                  <Typography className="left-heading">
                    Smarter work,
                    <br />
                    <span>powered by AI.</span>
                  </Typography>

                  {/* Description */}
                  <Typography className="left-description">
                    Fusion helps HDFC ERGO teams access HR, legal, product and
                    policy insights through AI-guided answers in seconds.
                  </Typography>

                  <Box
                    sx={{
                      marginTop: "32px",
                      width: "75%",
                      background: "#FFFFFF",
                      border: "1px solid #E8E8E8",
                      borderRadius: "14px",
                      boxShadow: "0px 8px 24px rgba(0,0,0,0.06)",
                      padding: "10px 14px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    {/* LEFT SIDE CONTENT (WORKSPACE + TEXT) */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "6px",
                        height: "45px",
                      }}
                    >
                      {/* WORKSPACE */}
                      <Typography
                        sx={{
                          fontSize: "11px",
                          fontWeight: 500,
                          letterSpacing: "1px",
                          color: "#999999",
                          marginBottom: "2px",
                          height: "16px",
                        }}
                      >
                        WORKSPACE
                      </Typography>

                      {/* FUSION MARKETPLACE + TYPEWRITER */}
                      <Typography
                        sx={{
                          fontSize: "15px",
                          fontWeight: 700,
                          color: "#1A1A1A",
                          letterSpacing: "-0.5px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Fusion Marketplace -{" "}
                        <span
                          style={{
                            display: "inline-block",
                            color: "#CC0000",
                            fontWeight: 600,
                          }}
                        >
                          <Typewriter
                            options={{
                              strings: [
                                "Know your policy",
                                "Legal Assist",
                                "Doc Assist",
                                "Accumulation of Risk",
                              ],
                              autoStart: true,
                              loop: true,
                              cursor: "_",
                              wrapperClassName: "inline-typewriter-wrapper",
                              cursorClassName: "inline-typewriter-cursor",
                              delay: 75,
                            }}
                          />
                        </span>
                      </Typography>
                    </Box>

                    {/* RIGHT SIDE ARROW BUTTON (VERTICALLY CENTERED) */}
                    <Box
                      sx={{
                        width: "40px",
                        height: "40px",
                        borderRadius: "50%",
                        background: "#CC0000",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <img
                        src={sendButton}
                        alt="message send"
                        style={{ transform: "rotate(90deg)" }}
                      />
                    </Box>
                  </Box>
                  {/* Feature Tags */}
                  <Box className="feature-tags">
                    <Box
                      className="feature-pill"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "3px",
                      }}
                    >
                      <LockOutlinedIcon
                        sx={{
                          fontSize: "14px",
                          color: "#CC0000",
                        }}
                      />
                      Secure internal access
                    </Box>
                    <Box className="feature-pill">
                      🤖 AI answers, human decisions
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Grid>
          )}

          <Box
            sx={{
              // background: "#ffff",
              background: "transparent",
              flex: 1,
              minHeight: "100vh",
              paddingTop: isSM ? "8rem" : "0rem",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: isSM ? "flex-start" : "center",
                height: "100%",
              }}
            >
              {/* <h1
                style={{
                  fontSize: "1.875rem",
                  fontWeight: 700,
                  letterSpacing: "0.0625rem",
                  textAlign: "center",
                  marginTop: 0,
                  marginBottom: 0,
                }}
              >
                WELCOME!
              </h1> */}
              <Box
                sx={{
                  // position: "absolute",
                  // top: "0.625rem",
                  // left: "50%",
                  // transform: "translateX(-45%)",
                  // zIndex: 1,
                  // padding: "1rem",
                  // backgroundColor: "white",
                  // width: "100%",
                  // display: "flex",
                  // justifyContent: "center",
                  // alignItems: "center",
                  textAlign: "center",
                  // background:
                  //   "linear-gradient(1deg, #ff817f, #fed0b2, #feebd7)",
                }}
              >
                <img
                  src={fusion}
                  alt="logo"
                  style={{
                    width: isSM ? "6.75rem" : "6.75rem",
                    height: "auto",
                    backgroundColor: "transparent",
                    marginTop: "-20px",
                  }}
                />
              </Box>
              <Typography className="ai-platform-label" sx={{ mb: 1 }}>
                AI-POWERED INTERNAL PLATFORM
              </Typography>
              <Box
                sx={{
                  fontSize: "20px",
                  fontWeight: 700,
                  letterSpacing: "0.0625rem",
                  textAlign: "center",
                  marginTop: 0,
                  marginBottom: 0,
                }}
              >
                Fusion
              </Box>
              <Box
                className="feature-pill"
                sx={{ marginBottom: "14px", color: "#666666" }}
              >
                🤖 AI suggestions, human decisions
              </Box>
              {/* <Box
                sx={{
                  padding: "1rem 1.5rem 1rem 1.5rem",
                  width: "90%",
                  maxWidth: "28.125rem",
                  margin: "0rem auto 0 auto",
                  borderRadius: "1.25rem",
                  // boxShadow: "0rem 0rem 3.75rem 0rem #7777771A",
                  background: "white",
                }}
              > */}
              <Box
                sx={{
                  padding: "27px 27px 1px 27px",
                  width: "90%",
                  maxWidth: "420px",
                  borderRadius: "16px",
                  background: "#ffffff",
                  boxShadow: "0px 10px 40px rgba(0,0,0,0.08)",
                }}
              >
                <Form onSubmit={handleRecaptcha}>
                  <Box
                    sx={{
                      width: "100%",
                      maxWidth: "360px",
                      mb: "7px",
                      height: "63.5px",
                    }}
                  >
                    <Typography
                      sx={{
                        height: "36px",
                        // fontFamily: "Inter",
                        color: "#1A1A1A",
                        fontSize: "24px",
                        fontWeight: 650,
                        letterSpacing: "0px",
                      }}
                    >
                      Sign in to Fusion
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: isSM ? "11px" : "12px",
                        fontWeight: 400,
                        color: "#999999",
                        mt: "4px",
                      }}
                    >
                      Sign in to access HR, legal, product and policy AI
                      assistant
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      position: "relative",
                      width: "100%",
                      "& .MuiInputBase-input": {
                        paddingLeft: "38px",
                      },
                    }}
                  >
                    <MailOutlineIcon
                      sx={{
                        position: "absolute",
                        left: "12px",
                        top: "39px",
                        fontSize: 16,
                        color: "#999999",
                        pointerEvents: "none",
                      }}
                    />

                    <CommonInput
                      id="username"
                      label="Email"
                      placeholder="Enter your email"
                      onChangeHandler={handleUsernameChange}
                      value={userName}
                    />
                  </Box>
                  {/* <CommonInput
                    id="password"
                    label="Password"
                    password
                    passwordIconOff={
                      <VisibilityOff sx={{ fontSize: "1.2rem" }} />
                    }
                    passwordIcon={<Visibility sx={{ fontSize: "1.2rem" }} />}
                    onClickHandler={handlePasswordVisibilityChange}
                    showPassword={showPassword}
                    placeholder="Enter your password"
                    onChangeHandler={handlePasswordChange}
                    value={passwordValue}
                  /> */}
                  <Box
                    sx={{
                      position: "relative",
                      width: "100%",
                      "& .MuiInputBase-input": {
                        paddingLeft: "38px",
                      },
                    }}
                  >
                    <LockOutlinedIcon
                      sx={{
                        position: "absolute",
                        left: "12px",
                        top: "38px",
                        fontSize: 16,
                        color: "#999999",
                        pointerEvents: "none",
                      }}
                    />

                    <CommonInput
                      id="password"
                      label="Password"
                      password
                      passwordIconOff={
                        <VisibilityOff sx={{ fontSize: "1.2rem" }} />
                      }
                      passwordIcon={<Visibility sx={{ fontSize: "1.2rem" }} />}
                      onClickHandler={handlePasswordVisibilityChange}
                      showPassword={showPassword}
                      placeholder="Enter your password"
                      onChangeHandler={handlePasswordChange}
                      value={passwordValue}
                    />
                  </Box>
                  {errorMessage && (
                    <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                      {errorMessage}
                    </Typography>
                  )}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      // mt: "6px",
                    }}
                  >
                    <a
                      href="https://pssm.hdfcergo.com/authorization.do"
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        fontSize: "0.8125rem", // 13px
                        fontWeight: 600,
                        color: "#CC0000",
                        textDecoration: "none",
                        cursor: "pointer",
                      }}
                    >
                      Forgot password?
                    </a>
                  </Box>
                  <Button
                    variant="contained"
                    sx={{
                      background: "#CC0000",
                      width: "100%",
                      borderRadius: "62.5rem",
                      fontSize: "0.8rem ",
                      fontWeight: "600",
                      marginTop: "1.0rem",
                      "&:hover": {
                        background: "#E8505B",
                      },
                    }}
                    disabled={loader}
                    type="submit"
                  >
                    {loader ? (
                      <CircularProgress size={24} style={{ color: "#fff" }} />
                    ) : (
                      "Sign in to Fusion"
                    )}
                  </Button>
                </Form>
                <p
                  style={{
                    textAlign: "center",
                    margin: "1rem 0",
                    fontSize: "12px",
                    letterSpacing: "0px",
                    fontWeight: 400,
                  }}
                >
                  <span style={{ color: "#999999" }}>
                    By proceeding, you agree to HDFC ERGO's
                  </span>
                  &nbsp;
                  <a
                    style={{ color: "#2161DD" }}
                    rel="noreferrer"
                    href="https://www.hdfcergo.com/terms-conditions/terms"
                    target="_blank"
                  >
                    Terms and Conditions.
                  </a>
                </p>

                {/* <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    flexDirection: "column",
                    width: "100%",
                    mt: 1.6,
                  }}
                > */}
                {/* <Box display={"flex"} alignItems={"center"} mb={0.5}>
                    <Typography fontWeight={500} mr={0.6} fontSize={13}>
                      Powered by
                    </Typography>
                    <img src={ctrlNLogo} alt="ctrl n" height={40} />
                  </Box> */}
                {/* <Support />
                </Box> */}
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: isSM ? "0 20px" : "0 77px",
                  mt: "12px",
                }}
              >
                <VerifiedUserOutlinedIcon
                  sx={{
                    fontSize: "13px",
                    color: "#999999",
                  }}
                />

                <Typography
                  sx={{
                    fontFamily: "Inter",
                    fontSize: "13px",
                    fontWeight: 400,
                    color: "#999999",
                    lineHeight: "18px",
                  }}
                >
                  Secure workspace for internal insurance teams.
                </Typography>
              </Box>
            </Box>
          </Box>
        </>
      )}
      <Modal
        open={openOTPModal}
        onClose={() => {
          setOpenOTPmodal(false);
          setOtpTimer(60);
        }}
        disableAutoFocus={true}
        disableEnforceFocus={true}
        hideBackdrop={false}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "40%",
            height: "40%",
            // overflow: "scroll",
            bgcolor: "background.paper",
            // border: "2px solid #fff",
            boxShadow: 24,
            borderRadius: "12px",
            // pt: 2,
            px: 3,
            pb: 2,
            background:
              "linear-gradient(90deg, #ff817f 0%, #fed0b2 0%, #ffffff 60%)",
          }}
        >
          <div className={"otp_container"}>
            <p
              style={{
                color: "black",
                fontSize: "1.6rem",
                marginBottom: "1rem",
                textAlign: "center",
              }}
            >
              Enter 4-digit OTP
            </p>
            <Grid container spacing={1} justifyContent="center">
              {otp.map((digit, index) => (
                <Grid item key={index}>
                  <input
                    ref={(el) => (otpRefs.current[index] = el)}
                    type="text"
                    value={digit}
                    maxLength={1}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(e, index)}
                    style={{
                      width: "50px",
                      height: "50px",
                      textAlign: "center",
                      fontSize: "18px",
                      border: `2px solid ${digit ? "green" : "gray"}`,
                      borderRadius: "6px",
                    }}
                  />
                </Grid>
              ))}
            </Grid>
            {successMessage && (
              <Typography
                variant="body2"
                color="success"
                sx={{ textAlign: "center", mt: 1 }}
              >
                <span style={{ color: "black" }}>{successMessage}</span>{" "}
                {otpTimer > 0 ? (
                  <span style={{ color: "red", fontWeight: 600 }}>
                    {otpTimer}
                  </span>
                ) : (
                  <b
                    style={{ textDecoration: "underline", cursor: "pointer" }}
                    onClick={() => {
                      setSuccessMessage(
                        "OTP sent to your registered mobile number.",
                      );
                      setOtpTimer(120);
                    }}
                  >
                    Resend OTP
                  </b>
                )}
              </Typography>
            )}
            {errorMessage && (
              <Typography
                variant="body2"
                color="error"
                sx={{ textAlign: "center", mt: 1 }}
              >
                {errorMessage}
              </Typography>
            )}
            <div className={"btn_div"} style={{ marginTop: "1rem" }}>
              <Button
                variant="contained"
                sx={{
                  background: "#0E0E0E",
                  width: "100%",
                  borderRadius: "0.3125rem",
                  fontSize: "0.8rem ",
                  fontWeight: "600",
                  padding: "0.5rem 1rem",
                  "&:hover": {
                    background: "#E8505B",
                  },
                }}
                type="submit"
                onClick={handleOtpVerify}
              >
                verify otp
              </Button>
            </div>
            {/* <div style={{ marginTop: "1rem", textAlign: "center" }}>
        {canResend ? (
          <Button variant="text" onClick={handleResend}>
            Resend OTP
          </Button>
        ) : (
          <Typography variant="body2" sx={{ fontSize: "1rem" }}>
            Resend in {otpTimer}s
          </Typography>
        )}
      </div> */}
          </div>
        </Box>
      </Modal>
    </Grid>
  );
}
